<script setup lang="ts">
import { ref } from 'vue'
import { auth, signInWithEmail, signInWithProvider } from '../lib/auth'
import { isOAuthConfigured } from '../lib/config'
import type { Provider } from '../types/auth'

defineProps<{ dismissable?: boolean }>()
const emit = defineEmits<{ (e: 'cancel'): void }>()

const email = ref('')
const password = ref('')
const oauth = isOAuthConfigured()

async function onEmailSubmit() {
  try {
    await signInWithEmail(email.value, password.value)
  } catch {
    /* error surfaced via auth.error */
  }
}

async function onProvider(provider: Exclude<Provider, 'Email'>) {
  try {
    await signInWithProvider(provider)
  } catch {
    /* error surfaced via auth.error */
  }
}
</script>

<template>
  <div class="signin scroll">
    <button v-if="dismissable" class="back" @click="emit('cancel')">← Back</button>
    <img src="/assets/icon-64.png" alt="SnipCell" class="logo" />
    <h1>Sign in to SnipCell</h1>
    <p class="lede">Snip from PDFs &amp; images straight into Excel.</p>

    <div class="providers">
      <button
        class="provider"
        :disabled="!oauth || auth.status === 'loading'"
        :title="oauth ? 'Sign in with Google' : 'Google sign-in not configured yet'"
        @click="onProvider('Google')"
      >
        <span class="g">G</span> Continue with Google
      </button>
      <button
        class="provider"
        :disabled="!oauth || auth.status === 'loading'"
        :title="oauth ? 'Sign in with Microsoft' : 'Microsoft sign-in not configured yet'"
        @click="onProvider('Microsoft')"
      >
        <span class="ms">⊞</span> Continue with Microsoft
      </button>
      <p v-if="!oauth" class="hint muted">Provider sign-in activates once Firebase config is added.</p>
    </div>

    <div class="divider"><span>or</span></div>

    <form class="email-form" @submit.prevent="onEmailSubmit">
      <input v-model="email" type="email" placeholder="Email" autocomplete="username" required />
      <input v-model="password" type="password" placeholder="Password" autocomplete="current-password" required />
      <button class="primary" type="submit" :disabled="auth.status === 'loading'">
        {{ auth.status === 'loading' ? 'Signing in…' : 'Sign in' }}
      </button>
    </form>

    <p v-if="auth.error" class="error">{{ auth.error }}</p>

    <p class="foot muted">
      No account? <a href="https://accounts.snipcell.com/signup" target="_blank" rel="noopener">Sign up</a>
    </p>
  </div>
</template>

<style scoped>
.signin {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 22px;
  gap: 8px;
  position: relative;
}
.back {
  position: absolute;
  top: 10px;
  left: 10px;
  border: none;
  background: none;
  color: var(--muted);
  font-size: 12px;
  padding: 2px 4px;
}
.back:hover { background: none; color: var(--brand); }
.logo { width: 56px; height: 56px; }
h1 { margin: 6px 0 0; font-size: 19px; }
.lede { color: var(--muted); margin: 2px 0 16px; }
.providers { width: 100%; max-width: 300px; display: flex; flex-direction: column; gap: 8px; }
.provider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px;
}
.provider .g { color: #4285f4; font-weight: 800; }
.provider .ms { color: #00a4ef; font-weight: 800; }
.hint { font-size: 11px; margin: 2px 0 0; }
.divider {
  width: 100%;
  max-width: 300px;
  border-top: 1px solid var(--line);
  margin: 14px 0 6px;
  position: relative;
}
.divider span {
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg);
  padding: 0 8px;
  color: var(--muted);
  font-size: 11px;
}
.email-form { width: 100%; max-width: 300px; display: flex; flex-direction: column; gap: 8px; }
.email-form input {
  padding: 9px 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  font-size: 13px;
}
.email-form input:focus { outline: none; border-color: var(--brand); }
.error {
  color: var(--danger);
  background: #fdeeee;
  border-radius: 6px;
  padding: 8px 10px;
  max-width: 300px;
  font-size: 12px;
}
.foot { margin-top: 14px; font-size: 12px; }
.foot a { color: var(--brand); }
</style>
